import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Table, TableBody, TableHead } from '@mui/material';
import CardsTableColumnsHead from './components/CardsTableColumnsHead';
import { CardsTableBodyRow, CardsTableProps } from './types';
import CardsTableCell from './components/CardsTableCell';
import CardsTableEmptyPlaceholder from './components/CardsTableEmptyPlaceholder';
import CardsTableLoadingIndicator from './components/CardsTableLoadingIndicator';
import CardsTableRow from './components/CardsTableRow';
import './CardsTable.css';

export const CardsTable = (props: CardsTableProps) => {
  const isColorMix = props.rowDividerType === 'color-mix';
  const rows = props.cellsData.bodyRows;

  // Grouped mode is opted into per table by the rows themselves, so the sticky
  // thead below never touches the other CardsTable consumers.
  const isGrouped = rows.some((row) => !!row.groupHeader);
  const boxRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  useLayoutEffect(() => {
    if (!isGrouped || !boxRef.current || !headRef.current) return;
    // Group headers stick just under the table header; its height is measured
    // rather than assumed because the head grows on the mobile font sizes.
    boxRef.current.style.setProperty('--cards-table-head-height', `${headRef.current.offsetHeight}px`);
  }, [isGrouped, rows.length, props.cellsData.headCells.length]);

  // Handle infinite scroll
  const { onEndReached } = props;
  useEffect(() => {
    if (!onEndReached) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // Trigger when user scrolls to within 200px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        if (onEndReached) {
          onEndReached();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onEndReached]);

  const renderRowContent = (bodyRow: CardsTableBodyRow) => {
    return bodyRow.components.map((component, cellIndex) => (
      <CardsTableCell
        key={bodyRow.key + props.cellsData.headCells[cellIndex].key}
        id={bodyRow.key + props.cellsData.headCells[cellIndex].key}
        isColorMix={isColorMix}
        cellWidth={props.cellsData.headCells[cellIndex].columnWidth}
      >
        {component}
      </CardsTableCell>
    ));
  };

  return (
    <Box
      ref={boxRef}
      className={isGrouped ? 'cards-table cards-table--grouped' : 'cards-table'}
      sx={{
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        position: 'relative',
        width: '100%',
        '@media (max-width: 768px)': {
          borderRadius: '8px',
        },
      }}
    >
      <Table
        key={`table-cols-${props.cellsData.headCells.length}`}
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '@media (max-width: 768px)': {
            fontSize: '12px',
          },
          '@media (max-width: 480px)': {
            fontSize: '10px',
          },
        }}
      >
        <TableHead ref={headRef}>
          <CardsTableColumnsHead {...props} />
        </TableHead>
        <TableBody>
          {rows.map((bodyRow, index) => (
            <React.Fragment key={bodyRow.key}>
            {bodyRow.groupHeader}
            <CardsTableRow
              index={index}
              isColorMix={isColorMix}
              isSelected={props.selectedCard?.id === bodyRow.card.id}
              className={bodyRow.className}
              onClick={() => {
                // Use row's onClick if provided, otherwise use default card select
                if (bodyRow.onClick) {
                  bodyRow.onClick();
                } else if (props.onCardSelect) {
                  props.onCardSelect(bodyRow.card);
                }
              }}
              onMouseEnter={bodyRow.onMouseEnter}
              onMouseLeave={bodyRow.onMouseLeave}
            >
              {renderRowContent(bodyRow)}
            </CardsTableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {!props.cellsData.bodyRows.length && !props.isLoading && (
        props.emptyPlaceholder ?? <CardsTableEmptyPlaceholder onImportClick={props.onImportClick} />
      )}
      {props.isLoading && <CardsTableLoadingIndicator />}
    </Box>
  );
};
